import { CalendarClock, Play, RefreshCw, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "../api";
import {
  ActionButton,
  EmptyState,
  ErrorState,
  LoadingState,
  Panel,
  SectionHeader,
  Tag,
} from "../components/design-system";
import { useResource } from "../hooks/use-resource";
import type { JobState } from "../types";
import { formatDateTime } from "../utils";

export function JobsPage() {
  const jobs = useResource(() => api.jobs(), [], 5_000);
  const [triggering, setTriggering] = useState<string | null>(null);

  const trigger = async (name: string) => {
    setTriggering(name);
    try {
      const result = await api.runJob(name);
      toast.success(
        result.accepted ? "任务已进入运行状态" : "任务已经在运行，无需重复触发",
      );
      await jobs.refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "任务触发失败");
    } finally {
      setTriggering(null);
    }
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        eyebrow="SCHEDULER"
        title="定时任务"
        desc={
          jobs.data
            ? `共 ${jobs.data.length} 项任务 · 所有任务均由后端集中调度`
            : "所有任务由后端集中调度"
        }
        className="rise-in"
        action={
          <ActionButton
            busy={jobs.refreshing}
            onClick={() => void jobs.refresh()}
          >
            <RefreshCw size={13} />
            刷新状态
          </ActionButton>
        }
      />

      {jobs.error && (
        <ErrorState error={jobs.error} onRetry={() => void jobs.refresh()} />
      )}
      {jobs.loading && !jobs.data ? (
        <Panel>
          <LoadingState rows={7} />
        </Panel>
      ) : jobs.data?.length ? (
        <>
          <Panel className="rise-in rise-in-1 hidden overflow-hidden md:block">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="border-b border-sand-light bg-cream/60 text-left text-[11px] uppercase tracking-wider text-ink-faint">
                  {[
                    "任务",
                    "执行周期",
                    "上次开始",
                    "上次完成",
                    "耗时",
                    "运行次数",
                    "状态",
                    "",
                  ].map((heading) => (
                    <th
                      key={heading}
                      className="whitespace-nowrap px-5 py-3.5 font-medium"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-sand-light/60">
                {jobs.data.map((job) => (
                  <tr key={job.name} className="transition hover:bg-cream/50">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${job.status === "failed" ? "bg-[#F3DDD3] text-destructive" : "bg-peach text-clay-deep"}`}
                        >
                          <CalendarClock size={15} strokeWidth={1.8} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-ink">{job.name}</p>
                          <p className="mt-0.5 max-w-[240px] truncate text-[11.5px] text-ink-faint">
                            {job.description}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-5 py-4">
                      <p className="text-[12px] font-medium text-ink-soft">
                        {job.scheduleLabel}
                      </p>
                      <p className="mt-1 text-[10.5px] text-ink-faint">
                        <code>{job.expression}</code> · {job.timezone}
                      </p>
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 font-mono text-[11.5px] text-ink-soft">
                      {formatDateTime(job.lastStartedAt, true)}
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 font-mono text-[11.5px] text-ink-soft">
                      {formatDateTime(job.lastFinishedAt, true)}
                    </td>
                    <td className="px-5 py-4 font-serif-num text-[14px]">
                      {duration(job)}
                    </td>
                    <td className="px-5 py-4 font-serif-num text-[15px]">
                      {job.runCount.toLocaleString()}
                    </td>
                    <td className="px-5 py-4">
                      <JobTag status={job.status} />
                      <p className="mt-1.5 whitespace-nowrap text-[10.5px] text-ink-faint">
                        {triggerLabel(job.lastTrigger)}
                      </p>
                    </td>
                    <td className="px-5 py-4">
                      <button
                        disabled={
                          Boolean(triggering) || job.status === "running"
                        }
                        onClick={() => void trigger(job.name)}
                        className="rounded-full border border-sand-light p-2 text-ink-soft transition hover:border-sand hover:text-clay-deep active:scale-90 disabled:opacity-35"
                        title="立即运行"
                      >
                        <Play size={13} fill="currentColor" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          <div className="space-y-3 md:hidden">
            {jobs.data.map((job, index) => (
              <Panel
                key={job.name}
                className={`rise-in rise-in-${Math.min(index + 1, 4)} p-5`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="eyebrow text-[10px] text-ink-faint">
                      {job.name}
                    </p>
                    <p className="mt-1 text-[14px] font-medium leading-snug">
                      {job.description}
                    </p>
                  </div>
                  <JobTag status={job.status} />
                </div>
                <code className="mt-3 inline-block rounded-md bg-cream-deep px-2 py-1 font-mono text-[11px] text-clay-deep">
                  {job.scheduleLabel} · {job.expression} · {job.timezone}
                </code>
                <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2.5 border-t border-dashed border-sand-light pt-4 text-[12px]">
                  <div>
                    <dt className="text-ink-faint">触发方式</dt>
                    <dd className="mt-0.5 text-[12px]">
                      {triggerLabel(job.lastTrigger)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-ink-faint">上次开始</dt>
                    <dd className="mt-0.5 font-mono text-[11px]">
                      {formatDateTime(job.lastStartedAt, true)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-ink-faint">上次完成</dt>
                    <dd className="mt-0.5 font-mono text-[11px]">
                      {formatDateTime(job.lastFinishedAt, true)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-ink-faint">耗时</dt>
                    <dd className="mt-0.5 font-serif-num text-[15px]">
                      {duration(job)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-ink-faint">运行次数</dt>
                    <dd className="mt-0.5 font-serif-num text-[15px]">
                      {job.runCount.toLocaleString()}
                    </dd>
                  </div>
                </dl>
                {job.lastError && (
                  <p className="mt-3 flex items-start gap-2 rounded-lg bg-[#F3DDD3] p-3 text-[11.5px] text-destructive">
                    <TriangleAlert size={14} className="mt-0.5 shrink-0" />
                    {job.lastError}
                  </p>
                )}
                <ActionButton
                  tone="dark"
                  busy={triggering === job.name || job.status === "running"}
                  disabled={Boolean(triggering && triggering !== job.name)}
                  onClick={() => void trigger(job.name)}
                  className="mt-4 w-full"
                >
                  <Play size={13} />
                  立即运行
                </ActionButton>
              </Panel>
            ))}
          </div>
        </>
      ) : (
        <Panel>
          <EmptyState
            title="没有已注册任务"
            description="在后端集中式 scheduler 中注册任务后会自动显示。"
          />
        </Panel>
      )}
    </div>
  );
}

function duration(job: JobState) {
  return job.lastDurationMs === null ? "—" : `${job.lastDurationMs} ms`;
}

function triggerLabel(trigger: JobState["lastTrigger"]): string {
  if (!trigger) return "尚未执行";
  const labels: Record<Exclude<JobState["lastTrigger"], null>, string> = {
    startup: "启动检查",
    schedule: "按时执行",
    "catch-up": "漏时补偿",
    manual: "管理员触发",
  };
  return labels[trigger];
}

function JobTag({ status }: { status: JobState["status"] }) {
  const tone =
    status === "failed"
      ? "danger"
      : status === "success"
        ? "sage"
        : status === "running"
          ? "clay"
          : "neutral";
  const label = {
    idle: "等待运行",
    running: "运行中",
    success: "最近成功",
    failed: "最近失败",
  }[status];
  return <Tag tone={tone}>{label}</Tag>;
}
